/**
 * Export Center Page — Real downloads connected to backend
 */
import React, { useState, useEffect } from 'react';
import {
  Download, FileText, Database, FileSpreadsheet,
  Package, CheckCircle2, Clock, Filter, Loader2,
  BarChart3, ScrollText, AlertCircle
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useToast } from '../../hooks/use-toast';
import { JourneySection, JourneyPanel } from '../journeys/JourneyPanel';
import { ReportAssembly, CDPResponse, XBRLExport as XBRLExportJourney } from '../journeys/JourneyReports';
import { apiFetch } from '../../services/api_service';


const API_URL = process.env.REACT_APP_BACKEND_URL;

const EXPORT_OPTIONS = [
  {
    id: 'compliance-summary',
    name: 'Compliance Summary',
    description: 'Complete platform overview with evidence, frameworks, obligations, and audit statistics',
    format: 'PDF',
    icon: CheckCircle2,
    endpoint: '/api/v1/reports/export/compliance-summary',
    includes: ['Evidence count', 'Framework coverage', 'Obligations', 'Audit events'],
  },
  {
    id: 'evidence-register',
    name: 'Evidence Register',
    description: 'All evidence documents with metadata, categories, and compliance status',
    format: 'CSV',
    icon: FileText,
    endpoint: '/api/v1/reports/export/evidence-register',
    includes: ['Document titles', 'Categories', 'Frameworks', 'Status'],
  },
  {
    id: 'audit-trail',
    name: 'Audit Trail Export',
    description: 'Complete audit trail for compliance verification and auditor review',
    format: 'CSV',
    icon: ScrollText,
    endpoint: '/api/v1/reports/export/audit-trail',
    includes: ['All events', 'User actions', 'Timestamps', 'Severity'],
  },
  {
    id: 'emissions-data',
    name: 'Emissions Data',
    description: 'GHG emissions across Scope 1, 2, and 3 with trends and breakdowns',
    format: 'XLSX',
    icon: BarChart3,
    endpoint: '/api/v1/reports/export/emissions-data',
    includes: ['Scope 1', 'Scope 2', 'Scope 3', 'YoY trends'],
  },
];

const FORMAT_COLORS = {
  PDF: 'text-red-400 bg-red-500/15 border-red-500/30',
  CSV: 'text-emerald-400 bg-emerald-500/15 border-emerald-500/30',
  XLSX: 'text-blue-400 bg-blue-500/15 border-blue-500/30',
};

export default function ExportCenterPage() {
  const { toast } = useToast();
  const [exporting, setExporting] = useState(null);
  const [filterFormat, setFilterFormat] = useState('all');
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  useEffect(() => {
    apiFetch(`/v1/reports/history`)
      .then(r => r.json())
      .then(d => setHistory(d.reports || []))
      .catch(() => {})
      .finally(() => setLoadingHistory(false));
  }, []);

  const handleExport = async (option) => {
    setExporting(option.id);
    try {
      const res = await fetch(`${API_URL}${option.endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!res.ok) throw new Error('Export failed');

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;

      const cd = res.headers.get('content-disposition');
      let filename = `${option.id}_${Date.now()}.${option.format.toLowerCase()}`;
      if (cd) {
        const m = /filename="?([^";\n]+)"?/.exec(cd);
        if (m) filename = m[1];
      }
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast({ title: 'Export Complete', description: `${option.name} downloaded successfully` });

      // Refresh history
      apiFetch(`/v1/reports/history`)
        .then(r => r.json())
        .then(d => setHistory(d.reports || []));
    } catch (e) {
      toast({ title: 'Export Failed', description: e.message, variant: 'destructive' });
    } finally {
      setExporting(null);
    }
  };

  const filtered = EXPORT_OPTIONS.filter(o => filterFormat === 'all' || o.format === filterFormat);
  const formats = [...new Set(EXPORT_OPTIONS.map(o => o.format))];

  return (
    <div className="space-y-6" data-testid="export-center-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Export Center</h1>
          <p className="text-sm text-[#6B6B6B] mt-1">Download compliance data, reports, and audit trails</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <StatCard icon={Download} color="emerald" label="Export Options" value={EXPORT_OPTIONS.length} />
        <StatCard icon={FileText} color="blue" label="Formats Available" value={formats.length} />
        <StatCard icon={CheckCircle2} color="purple" label="Reports Generated" value={history.length} />
        <StatCard icon={Clock} color="amber" label="Last Export" value={
          history.length > 0 ? new Date(history[0]?.generated_at).toLocaleDateString() : 'Never'
        } />
      </div>

      {/* Filter */}
      <div className="flex items-center gap-3">
        <Filter size={14} className="text-[#6B6B6B]" />
        <FilterBtn active={filterFormat === 'all'} onClick={() => setFilterFormat('all')}>All Formats</FilterBtn>
        {formats.map(f => (
          <FilterBtn key={f} active={filterFormat === f} onClick={() => setFilterFormat(f)}>{f}</FilterBtn>
        ))}
      </div>

      {/* Export Options */}
      <div className="space-y-3">
        {filtered.map(option => {
          const Icon = option.icon;
          const isExporting = exporting === option.id;
          const fmtColor = FORMAT_COLORS[option.format] || FORMAT_COLORS.CSV;
          const lastExport = history.find(h => h.report_type === option.id.replace(/-/g, '_'));

          return (
            <div
              key={option.id}
              className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-5 hover:bg-[#151515] hover:border-[#2A2A2A] transition-all"
              data-testid={`export-option-${option.id}`}
            >
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-lg bg-[#1F1F1F]">
                  <Icon size={20} className="text-[#A0A0A0]" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-base font-semibold text-white">{option.name}</h3>
                    <span className={`px-2 py-0.5 text-[10px] font-bold rounded border ${fmtColor}`}>
                      {option.format}
                    </span>
                  </div>
                  <p className="text-sm text-[#6B6B6B] mb-2">{option.description}</p>
                  <div className="flex items-center gap-2 flex-wrap">
                    {option.includes.map(item => (
                      <span key={item} className="px-2 py-0.5 text-[10px] bg-[#0A0A0A] text-[#6B6B6B] rounded">{item}</span>
                    ))}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2">
                  {lastExport && (
                    <div className="text-[10px] text-[#6B6B6B] flex items-center gap-1">
                      <Clock size={10} />
                      Last: {new Date(lastExport.generated_at).toLocaleDateString()}
                    </div>
                  )}
                  <Button
                    size="sm"
                    disabled={isExporting}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5 disabled:opacity-50"
                    onClick={() => handleExport(option)}
                    data-testid={`export-btn-${option.id}`}
                  >
                    {isExporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                    {isExporting ? 'Exporting...' : 'Download'}
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Recent Export History */}
      {history.length > 0 && (
        <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-5">
          <h3 className="text-sm font-semibold text-[#A0A0A0] mb-3">Recent Exports</h3>
          <div className="space-y-2">
            {history.slice(0, 5).map((h, i) => (
              <div key={i} className="flex items-center justify-between text-xs py-2 border-b border-[#1A1A1A] last:border-0">
                <div className="flex items-center gap-2">
                  <FileText size={12} className="text-[#6B6B6B]" />
                  <span className="text-white">{(h.report_type || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</span>
                  {h.framework && <span className="text-[#6B6B6B]">({h.framework})</span>}
                </div>
                <span className="text-[#6B6B6B]">{new Date(h.generated_at).toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Live User Journeys */}
      <JourneySection title="Live Journeys">
        <JourneyPanel title="CSRD Report Assembly" ucId="UC-18"><ReportAssembly /></JourneyPanel>
        <JourneyPanel title="CDP Questionnaire Auto-Fill" ucId="UC-19"><CDPResponse /></JourneyPanel>
        <JourneyPanel title="XBRL/iXBRL Export" ucId="UC-20"><XBRLExportJourney /></JourneyPanel>
      </JourneySection>
    </div>
  );
}

function StatCard({ icon: Icon, color, label, value }) {
  const bg = `bg-${color}-500/20`;
  const text = `text-${color}-400`;
  return (
    <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${bg}`}>
          <Icon size={18} className={text} />
        </div>
        <div>
          <div className="text-2xl font-bold text-white">{value}</div>
          <div className="text-xs text-[#6B6B6B]">{label}</div>
        </div>
      </div>
    </div>
  );
}

function FilterBtn({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
        active
          ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
          : 'bg-[#1F1F1F] text-[#6B6B6B] hover:text-white'
      }`}
    >
      {children}
    </button>
  );
}
